import { ContentCard } from "@msuite/ui/content-card";
import { Copy } from "@msuite/ui/copy";
import { DataGrid } from "@msuite/ui/data-grid";
import { icon } from "@msuite/ui/icons";
import { Email, Link, Phone } from "@msuite/ui/link";
import { useTranslation } from "@msuite/ui/localization";
import { formatIban, guardString } from "@msuite/utils/strings";
import type { IncomingInvoiceDetail, Nullable } from "@msuite/utils/types";

/** Props Interface */
interface IncomingInvoiceIssuerProps {
	incomingInvoice: Nullable<IncomingInvoiceDetail>;
}

export function IncomingInvoiceIssuer({ incomingInvoice }: IncomingInvoiceIssuerProps) {
	/** Hooks */
	const { t } = useTranslation();

	/** Computed */
	// One line each, so the card stays as short as the invoice card beside it.
	const address = [
		[incomingInvoice?.issuerStreet, incomingInvoice?.issuerHouseNumber].filter(Boolean).join(" "),
		[incomingInvoice?.issuerZipCode, incomingInvoice?.issuerCity].filter(Boolean).join(" "),
		incomingInvoice?.issuerCountry,
	]
		.filter(Boolean)
		.join(", ");
	const taxIds = [incomingInvoice?.issuerVatId, incomingInvoice?.issuerTaxNumber]
		.filter(Boolean)
		.join(" · ");

	/** Render */
	return (
		<ContentCard
			title={t("incoming_invoices.issuer")}
			icon={icon("Building2")}
		>
			<DataGrid>
				<DataGrid.Row>
					<DataGrid.Label>{t("incoming_invoices.contact")}</DataGrid.Label>
					<DataGrid.Value>
						{incomingInvoice?.contact ? (
							<Link href={`/contacts/${incomingInvoice.contact.id}`}>
								{incomingInvoice.contact.name}
							</Link>
						) : (
							guardString(incomingInvoice?.issuerName)
						)}
					</DataGrid.Value>
				</DataGrid.Row>
				<DataGrid.Row>
					<DataGrid.Label>{t("incoming_invoices.issuer_address")}</DataGrid.Label>
					<DataGrid.Value>{guardString(address)}</DataGrid.Value>
				</DataGrid.Row>
				<DataGrid.Row>
					<DataGrid.Label>{t("incoming_invoices.tax_ids")}</DataGrid.Label>
					<DataGrid.Value>{guardString(taxIds)}</DataGrid.Value>
				</DataGrid.Row>
				<DataGrid.Row>
					<DataGrid.Label>{t("incoming_invoices.issuer_email")}</DataGrid.Label>
					<DataGrid.Value>
						<Email>{guardString(incomingInvoice?.issuerEmail)}</Email>
					</DataGrid.Value>
				</DataGrid.Row>
				<DataGrid.Row>
					<DataGrid.Label>{t("incoming_invoices.issuer_phone")}</DataGrid.Label>
					<DataGrid.Value>
						<Phone>{guardString(incomingInvoice?.issuerPhone)}</Phone>
					</DataGrid.Value>
				</DataGrid.Row>
				<DataGrid.Divider />
				<DataGrid.Row>
					<DataGrid.Label>{t("incoming_invoices.iban")}</DataGrid.Label>
					<DataGrid.Cell>
						<Copy
							value={incomingInvoice?.iban}
							className="font-mono"
						>
							{formatIban(incomingInvoice?.iban)}
						</Copy>
					</DataGrid.Cell>
				</DataGrid.Row>
				<DataGrid.Row>
					<DataGrid.Label>{t("incoming_invoices.bic")}</DataGrid.Label>
					<DataGrid.Cell>
						<Copy
							value={incomingInvoice?.bic}
							className="font-mono"
						>
							{guardString(incomingInvoice?.bic)}
						</Copy>
					</DataGrid.Cell>
				</DataGrid.Row>
			</DataGrid>
		</ContentCard>
	);
}
